import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useExitAnim } from "../../hooks/useExitAnim";
import { supabase } from "../../lib/supabase";
import { MiniBubble } from "./ChatBubble";
import type { MiniBubbleMessage } from "./ChatBubble";
import { CopyConversationButton } from "./CopyConversationButton";
import { serializeConversation } from "../../utils/conversationToText";
import { OutboundLogView, type OutboundMessage } from "./OutboundLogView";
import type { DailyBreakdownRow } from "../../hooks/useSetterDailyBreakdown";

// =============================================================================
// SESSION MESSAGES MODAL
// =============================================================================
// PURPOSE: Shows all messages sent during a session/work day in a 3-column grid.
// Opened when user clicks a row in the WorkPatternDrilldown daily table.
//
// DATA FLOW:
// 1. Query setter's outbound messages in the time window → get conversation IDs
// 2. Fetch ALL messages (lead + setter) from those conversations in the window
// 3. Group by conversation, display in 3-col grid with chat bubbles
//
// CLICK-THROUGH: Card headers open ConversationViewModal for full thread.
// =============================================================================

interface Message extends MiniBubbleMessage {
  id: string;
  conversation_id: string;
}

interface ConversationGroup {
  conversation_id: string;
  contact_name: string | null;
  lead_instagram: string | null;
  messages: Message[];
  setter_count: number;
}

interface SessionMessagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  setterId: string;
  setterName: string;
  /** Start of the time window (ISO timestamp) */
  sessionStart: string;
  /** End of the time window (ISO timestamp) */
  sessionEnd: string;
  /** Display label for the header (e.g. "Mon, Feb 10" or "Session 3") */
  label: string;
  /** Sessions the day-row recorded — forwarded to OutboundLogView's reassignment-aware empty state. REQUIRED, not optional. */
  sessionCount: number;
  timezone: string;
  /** Every setter on the page, id + display name. Drives the switcher pills. */
  roster: { id: string; name: string }[];
  /** EVERY setter's daily rows, not just this one's — the switcher reads them to
   *  know who worked the selected day and what window each of them had. */
  allDailyRows: DailyBreakdownRow[];
  /** The org-tz work_date for a day-row open, `null` for a clock-arc open.
   *  null hides the switcher: an arc is one of N sessions and no other setter
   *  has an equivalent of "Alice's 3rd session" to switch to. */
  workDate: string | null;
  onConversationClick?: (
    conversationId: string,
    contactName: string,
    leadInstagram: string | null
  ) => void;
}

const formatDateTime = (dateStr: string, tz: string) =>
  new Date(dateStr).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
  });

// ─── Conversation Card ───────────────────────────────────────────────────────

const ConversationCard: React.FC<{
  group: ConversationGroup;
  timezone: string;
  sessionStart: string;
  sessionEnd: string;
  onHeaderClick?: () => void;
}> = ({ group, timezone, sessionStart, sessionEnd, onHeaderClick }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const firstInSessionRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to first in-session message when card mounts
  useEffect(() => {
    if (firstInSessionRef.current && scrollRef.current) {
      firstInSessionRef.current.scrollIntoView({ block: "center" });
    }
  }, []);

  const sessionStartTime = new Date(sessionStart).getTime();
  const sessionEndTime = new Date(sessionEnd).getTime();

  let foundFirst = false;

  return (
    <div
      data-testid="conversation-card"
      data-conversation-id={group.conversation_id}
      className="border border-black/[0.08] rounded-lg overflow-hidden liquid-glass anim-lift"
    >
      {/* Header */}
      <div
        className={`flex items-center gap-2 px-3 py-2 bg-black/[0.03] border-b border-black/[0.08] ${
          onHeaderClick ? "cursor-pointer hover:bg-black/[0.05] transition-colors" : ""
        }`}
        onClick={onHeaderClick}
      >
        <div className="flex-1 min-w-0">
          <span className="token-11 font-medium text-glass truncate block">
            {group.contact_name || "Unknown"}
          </span>
          {group.lead_instagram && (
            <span
              data-testid="conversation-card-handle"
              className="token-10 text-glass-muted truncate block"
            >
              @{group.lead_instagram}
            </span>
          )}
        </div>
        <span className="token-10 shrink-0 bg-ds-accent/20 text-ds-accent px-1.5 py-0.5 rounded">
          {group.setter_count} sent
        </span>
        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <CopyConversationButton
            variant="card"
            disabled={!group.messages.length}
            getText={() =>
              serializeConversation({
                messages: group.messages,
                contactName: group.contact_name || "Unknown",
                leadInstagram: group.lead_instagram,
                timezone,
              })
            }
          />
        </div>
        {onHeaderClick && (
          <svg
            className="w-3 h-3 text-glass-muted shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="max-h-72 overflow-y-auto p-2 bg-black/[0.03]">
        {group.messages.length === 0 ? (
          <p className="token-10 text-glass-muted text-center py-4">(no messages)</p>
        ) : (
          group.messages.map((msg) => {
            const msgTime = new Date(msg.sent_at).getTime();
            const isContext = msgTime < sessionStartTime || msgTime > sessionEndTime;
            const isFirstInSession = !isContext && !foundFirst;
            if (isFirstInSession) foundFirst = true;
            return (
              <div key={msg.id} ref={isFirstInSession ? firstInSessionRef : undefined}>
                <MiniBubble
                  message={msg}
                  timezone={timezone}
                  isContext={isContext}
                  showDate={false}
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ─── Main Modal ──────────────────────────────────────────────────────────────

export const SessionMessagesModal: React.FC<SessionMessagesModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  setterId,
  setterName,
  sessionStart,
  sessionEnd,
  label,
  sessionCount,
  timezone,
  roster,
  allDailyRows,
  workDate,
  onConversationClick,
}) => {
  const [groups, setGroups] = useState<ConversationGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<"timeline" | "grouped">("timeline");
  const [outbound, setOutbound] = useState<OutboundMessage[]>([]);
  const [outboundLoading, setOutboundLoading] = useState(false);
  const [outboundError, setOutboundError] = useState<string | null>(null);
  // Keep the modal mounted through the 200ms exit so anim-modal-out plays.
  const { mounted, closing } = useExitAnim(isOpen, 200);

  // ── Setter switcher ────────────────────────────────────────────────────────
  const [activeSetterId, setActiveSetterId] = useState(setterId);
  // Request generation. Neither fetch has an AbortController, and both call
  // their setters unconditionally on resolve, so without this a slow response
  // for setter A can paint under setter B's header — permanently, with no error.
  const genRef = useRef(0);

  const selectSetter = (id: string) => {
    // LOAD-BEARING, not defensive polish. The active pill is deliberately NOT
    // disabled, so clicking your own selected setter is a legal action. Without
    // this early return that click bumps genRef and then calls
    // setActiveSetterId with the value it already holds — which React bails out
    // of. No re-render, no dep change, so neither fetch re-fires, and any
    // request still in flight is then discarded by its own generation check.
    // Result: a setter with hundreds of messages renders the genuine "no
    // messages" empty state. No error, nothing visually distinct from a truly
    // empty day.
    if (id === activeSetterId) return;
    genRef.current += 1;
    setActiveSetterId(id);
  };

  useEffect(() => {
    genRef.current += 1;
    setActiveSetterId(setterId);
  }, [setterId, isOpen]);

  const rowFor = (id: string) =>
    workDate ? allDailyRows.find((r) => r.setter_id === id && r.work_date === workDate) : undefined;
  const activeRow = rowFor(activeSetterId);

  // The `activeSetterId === setterId` guard preserves arc mode byte-for-byte:
  // an arc click keeps the exact narrow window it was given and never widens to
  // the whole day row.
  const effSetterId = activeSetterId;
  const effSetterName =
    activeRow?.setter_name ?? roster.find((r) => r.id === activeSetterId)?.name ?? setterName;
  const effStart =
    activeSetterId === setterId ? sessionStart : (activeRow?.first_msg_time ?? sessionStart);
  const effEnd =
    activeSetterId === setterId ? sessionEnd : (activeRow?.last_msg_time ?? sessionEnd);
  const effSessionCount =
    activeSetterId === setterId ? sessionCount : (activeRow?.session_count ?? 0);

  const fetchMessages = useCallback(async () => {
    if (!supabase || !isOpen) return;
    const gen = ++genRef.current;
    setIsLoading(true);
    setError(null);

    try {
      // Step 1: Find conversations where this setter sent messages in the time window
      // Paginated + deterministically sorted — an unordered scan capped at PostgREST's
      // 1000-row default silently drops conversations, and which ones drop is
      // nondeterministic run-to-run without an ORDER BY.
      const CONVO_PAGE = 1000;
      let convoOffset = 0;
      const setterMsgs: { conversation_id: string }[] = [];
      for (;;) {
        const { data, error: e1 } = await supabase
          .from("ghl_messages")
          .select("conversation_id")
          .eq("organization_id", organizationId)
          .eq("sender_type", "setter")
          .not("ghl_sender_user_id", "is", null)
          // TODO: Hardcoded to Instagram — may miss other channel types if org uses SMS/email
          .eq("message_type", "TYPE_INSTAGRAM")
          .gte("sent_at", effStart)
          .lte("sent_at", effEnd)
          .order("sent_at", { ascending: true })
          .order("id", { ascending: true })
          .range(convoOffset, convoOffset + CONVO_PAGE - 1);

        if (e1) throw e1;
        const page = data ?? [];
        setterMsgs.push(...page);
        if (page.length < CONVO_PAGE) break;
        convoOffset += CONVO_PAGE;
      }

      if (setterMsgs.length === 0) {
        // Guard 1 of 3. This early-return branch is only reachable for a setter
        // with zero messages on the chosen day, so a fast switch INTO one and
        // back out would otherwise wipe the correct, currently-displayed list.
        if (gen !== genRef.current) return;
        setGroups([]);
        return;
      }

      // Filter to conversations assigned to this setter
      const uniqueConvoIds = [...new Set(setterMsgs.map((m) => m.conversation_id))];

      // Step 2: Get conversation metadata + verify assignment
      const { data: convos, error: e2 } = await supabase
        .from("ghl_conversations")
        .select("id, contact_name, lead_instagram, ghl_assigned_user_id")
        .in("id", uniqueConvoIds)
        .eq("ghl_assigned_user_id", effSetterId);

      if (e2) throw e2;
      const assignedIds = (convos || []).map((c) => c.id);
      if (assignedIds.length === 0) {
        // Guard 2 of 3 — the reassignment branch, same reasoning as guard 1.
        if (gen !== genRef.current) return;
        setGroups([]);
        return;
      }

      // Step 3: Fetch messages from those conversations with context
      // Expand the window backwards by 6 hours so we see what the lead said
      // before the setter replied (especially important for narrow session-arc clicks)
      const contextStart = new Date(
        new Date(effStart).getTime() - 6 * 60 * 60 * 1000
      ).toISOString();

      const CHUNK_SIZE = 100;
      const chunks: string[][] = [];
      for (let i = 0; i < assignedIds.length; i += CHUNK_SIZE) {
        chunks.push(assignedIds.slice(i, i + CHUNK_SIZE));
      }

      // Paginated per chunk — .limit(5000) was a FALSE GUARD, PostgREST caps at
      // 1000 regardless. A single 100-conversation chunk can exceed 1000 rows
      // once lead replies and 6h of context are included.
      const fetchChunkMessages = async (chunk: string[]): Promise<Message[]> => {
        const CTX_PAGE = 1000;
        let chunkOffset = 0;
        const rows: Message[] = [];
        for (;;) {
          const { data, error } = await supabase!
            .from("ghl_messages")
            .select(
              "id, message_text, sent_at, sender_type, conversation_id, has_media, ghl_sender_user_id, sender_name, message_type"
            )
            .in("conversation_id", chunk)
            .gte("sent_at", contextStart)
            .lte("sent_at", effEnd)
            .not(
              "message_type",
              "in",
              "(TYPE_ACTIVITY_OPPORTUNITY,TYPE_INTERNAL_COMMENT,TYPE_CALL)"
            )
            .or("sender_type.neq.setter,ghl_sender_user_id.not.is.null")
            .order("sent_at", { ascending: true })
            .order("id", { ascending: true })
            .range(chunkOffset, chunkOffset + CTX_PAGE - 1);

          if (error) throw error;
          const page = data ?? [];
          rows.push(...(page as unknown as Message[]));
          if (page.length < CTX_PAGE) break;
          chunkOffset += CTX_PAGE;
        }
        return rows;
      };

      const results = await Promise.all(chunks.map(fetchChunkMessages));

      // Merge and filter
      const allMsgs: Message[] = [];
      for (const rows of results) {
        for (const row of rows) {
          if (
            !row.message_text?.includes("Opportunity created") &&
            !row.message_text?.includes("Opportunity updated") &&
            !row.message_text?.includes("Opportunity deleted")
          ) {
            allMsgs.push(row);
          }
        }
      }

      // Step 4: Group by conversation
      const convoMap = new Map<string, { meta: (typeof convos)[0]; messages: Message[] }>();
      for (const c of convos || []) {
        convoMap.set(c.id, { meta: c, messages: [] });
      }
      for (const msg of allMsgs) {
        convoMap.get(msg.conversation_id)?.messages.push(msg);
      }

      // Build groups sorted by setter message count DESC (busiest conversations first)
      const built: ConversationGroup[] = [];
      convoMap.forEach(({ meta, messages }) => {
        if (messages.length === 0) return;
        built.push({
          conversation_id: meta.id,
          contact_name: meta.contact_name,
          lead_instagram: meta.lead_instagram,
          messages,
          setter_count: messages.filter((m) => m.sender_type === "setter").length,
        });
      });
      built.sort((a, b) => b.setter_count - a.setter_count);

      // Guard 3 of 3 — the main success path.
      if (gen !== genRef.current) return;
      setGroups(built);
    } catch (err) {
      console.warn("Failed to fetch session messages:", err);
      const msg =
        (err as Record<string, unknown>)?.message ??
        (err as Record<string, unknown>)?.details ??
        String(err);
      setError(String(msg));
    } finally {
      setIsLoading(false);
    }
    // isOpen and organizationId are KEPT deliberately: dropping isOpen stops the
    // modal refetching on open, dropping organizationId stops an org switch
    // invalidating. Only the three setter/window entries become effective.
  }, [isOpen, organizationId, effSetterId, effStart, effEnd]);

  const fetchOutbound = useCallback(async () => {
    if (!supabase || !isOpen) return;
    const gen = ++genRef.current;
    setOutboundLoading(true);
    setOutboundError(null);

    try {
      // ── ONE round trip. The reply-lag walk now runs server-side. ───────────
      // get_setter_timeline runs the exact attachReplyLags walk as a LAG()
      // window function in Postgres and returns the display rows WITH
      // reply_lag_mins already on them. It REPLACES the old two-phase fetch: an
      // outbound query, then BOTH sides of every conversation over a 168h
      // lookback, then attachReplyLags in JS.
      //
      // MEASURED S94: the old path pulled 1,295 rows / 445 KB across 4 round
      // trips for Norman's Aug-13; the RPC ships ~429 display rows in 1 trip
      // (~190 KB), the 168h context never leaving Postgres. The window function
      // is proven equal to attachReplyLags live: 269 vs 268 lags, median 30.9m
      // identical, across all three setters. This is why the old "paint rows
      // first, badges second" two-phase split is gone — there is no slow second
      // phase left to hide; rows and badges arrive together, fast.
      //
      // p_lookback_hours STAYS 168. Norman's slowest genuine reply measured
      // 72.6h, and a shorter window blanks that badge, which reads identically
      // to a cold blast. The constant now lives in ONE place — this arg and the
      // RPC's guard comment — instead of a client-side LOOKBACK_MS.
      const { data, error } = await supabase.rpc("get_setter_timeline", {
        p_organization_id: organizationId,
        p_setter_id: effSetterId,
        p_start: effStart,
        p_end: effEnd,
        p_lookback_hours: 168,
      });
      if (error) throw error;

      // The generation guard still matters: a slow response for setter A must
      // not paint under setter B's header after a fast switch. The RPC returns a
      // json array (RETURNS json, so PGRST_MAX_ROWS=1000 can't silently truncate
      // a heavy setter-day), already flattened to the OutboundMessage shape.
      if (gen !== genRef.current) return;
      setOutbound((data ?? []) as OutboundMessage[]);
    } catch (err) {
      console.warn("Failed to fetch setter timeline:", err);
      const msg =
        (err as Record<string, unknown>)?.message ??
        (err as Record<string, unknown>)?.details ??
        String(err);
      // Only the CURRENT request may raise an error banner. A stale fetch that
      // rejects after the user already switched setters would otherwise paint
      // an error over the new setter's perfectly good rows.
      if (gen === genRef.current) setOutboundError(String(msg));
    } finally {
      // Generation-guarded: a stale request must not hide the spinner while the
      // CURRENT request is still in flight, or the previous setter's empty list
      // reads as finished-loading.
      if (gen === genRef.current) setOutboundLoading(false);
    }
    // Same rule as fetchMessages: isOpen and organizationId stay.
  }, [isOpen, organizationId, effSetterId, effStart, effEnd]);

  // Timeline is the default tab, so the outbound fetch is EAGER on open.
  useEffect(() => {
    if (isOpen) fetchOutbound();
  }, [fetchOutbound, isOpen]);

  // Clear BOTH lists the instant the effective setter or window changes.
  // Without this, switching setter leaves the previous setter's data under the
  // new setter's name: `groups` because handleViewChange's lazy guard never
  // re-fires while already on "grouped", and `outbound` because nothing ever
  // resets it — it is only overwritten when a fetch resolves. An empty list for
  // 300ms is strictly better than a confidently wrong one.
  // fetchOutbound re-fires on its own via the eager effect above, so only the
  // grouped fetch needs the explicit call.
  // LINT: this intentionally omits `view` and `fetchMessages` and WILL raise one
  // react-hooks/exhaustive-deps warning. That warning is budgeted — including
  // either dep would re-clear both lists on every tab toggle. Do not silence it
  // with a disable comment; the 3-warning count across these files is the gate.
  useEffect(() => {
    setOutbound([]);
    setGroups([]);
    if (view === "grouped") fetchMessages();
  }, [effSetterId, effStart, effEnd]);

  // Grouped ("By conversation") fetch is LAZY — fired on first switch to that
  // tab, not on mount. Keeps net round trips on open at ONE.
  const handleViewChange = (next: "timeline" | "grouped") => {
    setView(next);
    if (next === "grouped" && groups.length === 0) fetchMessages();
  };

  // ESC key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  // Stats for header
  const stats = useMemo(() => {
    const totalMsgs = groups.reduce((s, g) => s + g.messages.length, 0);
    const setterMsgs = groups.reduce((s, g) => s + g.setter_count, 0);
    return { conversations: groups.length, totalMsgs, setterMsgs };
  }, [groups]);

  if (!mounted) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] overflow-y-auto"
      style={{ overscrollBehavior: "contain" }}
    >
      {/* Backdrop */}
      <div
        className={`${closing ? "anim-backdrop-out" : "anim-backdrop-in"} fixed inset-0 bg-black/50 backdrop-blur-sm`}
        onClick={onClose}
      />

      {/* Modal — anim-modal-in on the NON-glass wrapper (glass panel can't take transforms).
          onClick closes on a click on the wrapper itself (empty area), not the card —
          this wrapper covers the backdrop, so it must own the outside-click-to-close. */}
      <div
        className={`${closing ? "anim-modal-out" : "anim-modal-in"} flex min-h-full items-center justify-center p-4`}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="relative w-full max-w-7xl detail-overlay rounded-xl shadow-xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.08] shrink-0">
            <div>
              <h2 data-testid="modal-header-title" className="token-18 text-glass">
                {effSetterName} — {label}
              </h2>
              <p data-testid="modal-header-window" className="token-14 text-glass-secondary mt-0.5">
                {formatDateTime(effStart, timezone)} – {formatDateTime(effEnd, timezone)}
                {view === "grouped" && !isLoading && !error && (
                  <span className="ml-2 text-glass-muted">
                    · {stats.conversations} conversations · {stats.setterMsgs} sent
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="anim-tap text-glass-muted hover:text-glass-secondary transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 px-6 pt-3 shrink-0">
            <button
              type="button"
              onClick={() => handleViewChange("timeline")}
              aria-selected={view === "timeline"}
              className={`px-3 py-1.5 rounded-lg token-13 font-medium anim-tap transition-colors ${
                view === "timeline"
                  ? "bg-ds-accent/15 text-ds-accent"
                  : "text-glass-muted hover:text-glass hover:bg-black/[0.03]"
              }`}
            >
              Timeline
            </button>
            <button
              type="button"
              onClick={() => handleViewChange("grouped")}
              aria-selected={view === "grouped"}
              className={`px-3 py-1.5 rounded-lg token-13 font-medium anim-tap transition-colors ${
                view === "grouped"
                  ? "bg-ds-accent/15 text-ds-accent"
                  : "text-glass-muted hover:text-glass hover:bg-black/[0.03]"
              }`}
            >
              By conversation
            </button>
          </div>

          {/* Setter switcher. Hidden entirely for an arc open (workDate null),
              for a single-setter org, and until allDailyRows arrives — a pill
              row that flips from all-disabled to all-enabled looks broken. */}
          {workDate !== null && roster.length > 1 && allDailyRows.length > 0 && (
            <div className="px-6 pt-2 shrink-0">
              <div
                data-testid="modal-setter-switch"
                className="flex gap-1 flex-wrap bg-black/[0.05] rounded-lg p-1"
              >
                {roster.map((s) => {
                  const has = Boolean(rowFor(s.id));
                  const active = s.id === activeSetterId;
                  return (
                    <button
                      key={s.id}
                      data-testid="modal-setter-pill"
                      data-setter-id={s.id}
                      data-active={active ? "true" : "false"}
                      disabled={!has && !active}
                      title={has ? undefined : "No activity on this day"}
                      onClick={() => has && selectSetter(s.id)}
                      className={`anim-tap px-3 py-1.5 rounded-md token-14 font-medium transition-colors ${
                        active
                          ? "bg-black/[0.08] text-glass shadow-sm"
                          : has
                            ? "text-glass-secondary hover:text-glass"
                            : "text-glass-inactive cursor-not-allowed"
                      }`}
                    >
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {view === "timeline" ? (
              <>
                {outboundLoading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    <span className="ml-3 text-glass-secondary">Loading messages...</span>
                  </div>
                )}

                {/* SILENT-ZERO GUARD: a failed fetch must render the error, never
                    fall through to OutboundLogView's "0 messages" empty state. */}
                {outboundError && (
                  <div className="bg-ds-red/10 border border-ds-red/30 rounded-lg p-4 text-ds-red text-sm">
                    Error: {outboundError}
                  </div>
                )}

                {!outboundLoading && !outboundError && (
                  <OutboundLogView
                    messages={outbound}
                    timezone={timezone}
                    sessionCount={effSessionCount}
                    onConversationClick={onConversationClick}
                  />
                )}
              </>
            ) : (
              <>
                {isLoading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    <span className="ml-3 text-glass-secondary">Loading messages...</span>
                  </div>
                )}

                {error && (
                  <div className="bg-ds-red/10 border border-ds-red/30 rounded-lg p-4 text-ds-red text-sm">
                    Error: {error}
                  </div>
                )}

                {!isLoading && !error && groups.length === 0 && (
                  <div className="text-center py-12 text-glass-muted">
                    No messages found in this time window.
                  </div>
                )}

                {!isLoading && !error && groups.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {groups.map((group) => (
                      <ConversationCard
                        key={group.conversation_id}
                        group={group}
                        timezone={timezone}
                        // eff*, not the raw props: the card dims out-of-window
                        // messages as "context", and after a switch the raw
                        // props still describe the PREVIOUS setter's window.
                        sessionStart={effStart}
                        sessionEnd={effEnd}
                        onHeaderClick={
                          onConversationClick
                            ? () =>
                                onConversationClick(
                                  group.conversation_id,
                                  group.contact_name || "Unknown",
                                  group.lead_instagram
                                )
                            : undefined
                        }
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
